import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, PageHeader, StatusBadge, Modal, Field, inputCls, inputErrorCls, PrimaryButton, EmptyState } from "@/components/lims";
import SampleQrButton from "@/components/SampleQr";
import { SealCheck, ArrowUUpLeft, Stamp } from "@phosphor-icons/react";
import { canReviewResults, canReleaseResults } from "@/lib/roles";
import SigningFields, { useSigning } from "../components/SigningFields";

function QueueTable({ title, subtitle, items, testidPrefix, renderActions }) {
  return (
    <section className="mb-8" aria-label={title}>
      <h2 className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-1">{title}</h2>
      <p className="text-xs text-slate-400 mb-3">{subtitle}</p>
      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-400">Queue is empty</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">Test</th>
                <th className="px-4 py-3 font-semibold">Sample</th>
                <th className="px-4 py-3 font-semibold">Result</th>
                <th className="px-4 py-3 font-semibold">Flag</th>
                <th className="px-4 py-3 font-semibold">Entered by</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} data-testid={`${testidPrefix}-${t.test_code}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-slate-500">{t.test_code}</span>
                    <div className="font-medium text-slate-900">{t.assay_name}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex items-center gap-1">
                      <Link to={`/samples/${t.sample_id}`} className="font-mono text-slate-500 hover:text-slate-900 hover:underline">{t.sample_code}</Link>
                      {t.sample_id && <SampleQrButton sampleId={t.sample_id} sampleCode={t.sample_code} />}
                    </div>
                    <div className="text-slate-500">{t.client_name || ""}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-900">{t.result_value} {t.result_unit}
                    {(t.spec_min != null || t.spec_max != null) && <div className="text-xs font-mono text-slate-400">spec {t.spec_min ?? ""}–{t.spec_max ?? ""}</div>}
                  </td>
                  <td className="px-4 py-3">{t.result_flag ? <StatusBadge status={t.result_flag} /> : <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{t.result_entered_by || "—"}</td>
                  <td className="px-4 py-3"><div className="flex justify-end gap-2">{renderActions(t)}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

export default function ReviewQueue() {
  const { user } = useAuth();
  const isReviewer = canReviewResults(user);
  const isReleaser = canReleaseResults(user);
  const [submitted, setSubmitted] = useState([]);
  const [reviewed, setReviewed] = useState([]);
  const [loading, setLoading] = useState(true);
  // action: {t, mode: "approve" | "reject" | "release"}
  const [action, setAction] = useState(null);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [releasedSample, setReleasedSample] = useState(null);
  // Approve and release are electronic signatures; reject is not — it sends
  // work back rather than attesting to it (issue #99).
  const signing = useSigning();

  const load = useCallback(async () => {
    setLoading(true);
    const [s, r] = await Promise.all([
      isReviewer ? api.get("/tests?status=Submitted") : Promise.resolve({ data: [] }),
      isReleaser ? api.get("/tests?status=Reviewed") : Promise.resolve({ data: [] }),
    ]);
    setSubmitted(s.data);
    setReviewed(r.data);
    setLoading(false);
  }, [isReviewer, isReleaser]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Opening the queue acknowledges review/release notifications (issue #94).
    api.post("/notifications/read").catch(() => {});
  }, []);

  const start = (t, mode) => {
    setAction({ t, mode });
    setComment("");
    setError("");
    signing.reset();
  };

  const SIGNED_MEANING = {
    approve: "First-level review",
    release: "Final release approval",
  };

  const confirm = async (e) => {
    e.preventDefault();
    const { t, mode } = action;
    const needsComment = mode === "reject" || mode === "release";
    if (needsComment && !comment.trim()) {
      setError(mode === "reject" ? "A rejection comment is required." : "A release reason is required.");
      return;
    }
    const meaning = SIGNED_MEANING[mode];
    if (meaning && !signing.password) {
      setError("Your password is required to sign this approval.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "release") {
        await api.post(`/tests/${t.id}/release`, { reason: comment.trim(), signing: signing.block() });
        setReleasedSample({ id: t.sample_id, code: t.sample_code });
        toast.success(`${t.test_code} released`);
      } else {
        await api.post(`/tests/${t.id}/review`, {
          decision: mode,
          comment: comment.trim() || null,
          ...(meaning ? { signing: signing.block() } : {}),
        });
        toast.success(mode === "approve" ? `${t.test_code} approved for release` : `${t.test_code} rejected back to analyst`);
      }
      signing.reset();
      setAction(null);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <PageHeader title="Review Queue" subtitle="First-level review, then QA release. Rejects return to the analyst with a comment." />

      {releasedSample && (
        <div
          role="status"
          aria-live="polite"
          data-testid="release-parent-sample-next-step"
          className="mb-6 rounded-lg border border-status-ok/30 bg-status-ok/10 p-4 text-sm text-status-ok"
        >
          <p className="font-medium">Result released. The parent sample still requires its own QA release.</p>
          <p className="mt-1">
            Next, <Link data-testid="release-parent-sample-link" to="/samples" className="font-semibold underline underline-offset-2">open Sample Status</Link>, find {releasedSample.code || "the parent sample"}, and choose <strong>Release</strong> after every child test is released.
          </p>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />)}</div>
      ) : !isReviewer && !isReleaser ? (
        <EmptyState icon={SealCheck} title="No review responsibilities" subtitle="Your role does not include review or release" />
      ) : (
        <>
          {isReviewer && (
            <QueueTable
              title="Supervisor review"
              subtitle="Submitted results awaiting first-level approval"
              items={submitted}
              testidPrefix="review-row"
              renderActions={(t) => (
                <>
                  <button data-testid={`approve-${t.test_code}`} onClick={() => start(t, "approve")} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-status-ok bg-status-ok/10 border border-status-ok/30 hover:bg-status-ok/15 transition-colors"><SealCheck size={14} /> Approve</button>
                  <button data-testid={`reject-${t.test_code}`} onClick={() => start(t, "reject")} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-status-critical bg-status-critical/10 border border-status-critical/30 hover:bg-status-critical/15 transition-colors"><ArrowUUpLeft size={14} /> Reject</button>
                </>
              )}
            />
          )}
          {isReleaser && (
            <QueueTable
              title="QA release"
              subtitle="Reviewed results awaiting final release. Only released results reach a client."
              items={reviewed}
              testidPrefix="release-row"
              renderActions={(t) => (
                <>
                  <button data-testid={`release-${t.test_code}`} onClick={() => start(t, "release")} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-status-ok bg-status-ok/10 border border-status-ok/30 hover:bg-status-ok/15 transition-colors"><Stamp size={14} /> Release</button>
                  <button data-testid={`qa-reject-${t.test_code}`} onClick={() => start(t, "reject")} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-status-critical bg-status-critical/10 border border-status-critical/30 hover:bg-status-critical/15 transition-colors"><ArrowUUpLeft size={14} /> Reject</button>
                </>
              )}
            />
          )}
        </>
      )}

      <Modal
        open={!!action}
        onClose={() => setAction(null)}
        title={action ? `${action.mode === "release" ? "Release" : action.mode === "approve" ? "Approve" : "Reject"} ${action.t.test_code}` : ""}
        testid="review-action-modal"
      >
        {action && (
          <form onSubmit={confirm} noValidate className="space-y-4">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
              <div className="text-slate-500">{action.t.assay_name} · {action.t.sample_code}</div>
              <div className="font-medium text-slate-900 mt-1">
                Result: {action.t.result_value} {action.t.result_unit} {action.t.result_flag && `(${action.t.result_flag})`}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">entered by {action.t.result_entered_by || "—"}</div>
            </div>
            <Field
              label={action.mode === "approve" ? "Comment (optional)" : action.mode === "reject" ? "Rejection comment (required)" : "Release reason (required)"}
              error={error}
              errorTestId="review-action-error"
            >
              <textarea
                data-testid="review-comment-input"
                value={comment}
                onChange={(e) => { setComment(e.target.value); setError(""); }}
                rows={2}
                className={`${inputCls} ${error ? inputErrorCls : ""}`}
              />
            </Field>
            {SIGNED_MEANING[action.mode] && (
              <SigningFields
                signing={signing}
                meaning={SIGNED_MEANING[action.mode]}
                error={error}
                disabled={busy}
              />
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setAction(null)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
              <PrimaryButton data-testid="confirm-review-action" disabled={busy}>
                {action.mode === "release" ? "Release result" : action.mode === "approve" ? "Approve" : "Reject back"}
              </PrimaryButton>
            </div>
          </form>
        )}
      </Modal>
    </Page>
  );
}
